import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Loader2, Phone, RefreshCw, Search } from 'lucide-react';
import type {
  CommunicationsActivityRow,
  CommunicationsCallLogTotals,
  CommunicationsIvrTotals,
} from '../../../models/MasterAdmin/communications';
import CommunicationsAlertDialog from '../../../components/Communications/CommunicationsAlertDialog';
import {
  commCardClass,
  commDesktopTableWrapClass,
  commMobileCardClass,
  commPrimaryBtnClass,
  commSecondaryBtnClass,
  commSectionDescClass,
  commSectionTitleClass,
  commSelectClass,
  commToolbarClass,
} from '../../../components/Communications/communicationsUi';
import {
  connectCommunicationsCall,
  getCommunicationsActivity,
  getCommunicationsSettings,
} from '../../../services/MasterAdmin/communicationsService';

const DAYS_OPTIONS = [7, 30, 90, 180];

type CallAlert = {
  tone: 'success' | 'error' | 'warning';
  title: string;
  message: string;
};

function formatAction(action: string | null): string {
  if (!action) return '—';
  const labels: Record<string, string> = {
    whatsapp_requested: 'WhatsApp requested',
    ivr_disabled: 'IVR disabled',
    no_action: 'No action',
    outbound_initiated: 'Outbound initiated',
    status_callback: 'Status update',
    answered: 'Answered',
    terminal: 'Completed',
  };
  return labels[action] || action;
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function isIncomingCall(direction: string | null): boolean {
  if (!direction) return false;
  const d = direction.toLowerCase();
  return d.includes('inbound') || d.includes('incoming');
}

function formatDirection(direction: string | null): string {
  if (!direction) return '—';
  if (isIncomingCall(direction)) return 'Incoming';
  if (direction.toLowerCase().includes('outbound') || direction.toLowerCase().includes('outgoing')) {
    return 'Outgoing';
  }
  return direction;
}

function normalizePhoneForCall(value: string | null): string | null {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  if (digits.length < 10) return null;
  return value.trim().startsWith('+') ? value.trim() : `+91${digits.slice(-10)}`;
}

const CommunicationsActivityPanel: React.FC = () => {
  const [days, setDays] = useState(30);
  const [callRows, setCallRows] = useState<CommunicationsActivityRow[]>([]);
  const [callTotals, setCallTotals] = useState<CommunicationsCallLogTotals | null>(null);
  const [callSource, setCallSource] = useState<'exotel_api' | 'local'>('exotel_api');
  const [callFetchError, setCallFetchError] = useState<string | null>(null);
  const [ivrRows, setIvrRows] = useState<CommunicationsActivityRow[]>([]);
  const [ivrTotals, setIvrTotals] = useState<CommunicationsIvrTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [callSearch, setCallSearch] = useState('');
  const [ivrSearch, setIvrSearch] = useState('');
  const [executivePhone, setExecutivePhone] = useState<string | null>(null);
  const [exoPhoneConfigured, setExoPhoneConfigured] = useState(false);
  const [callingRowId, setCallingRowId] = useState<string | null>(null);
  const [callAlert, setCallAlert] = useState<CallAlert | null>(null);

  const showCallAlert = (alert: CallAlert) => setCallAlert(alert);

  const loadSettings = useCallback(async () => {
    try {
      const settings = await getCommunicationsSettings();
      setExecutivePhone(settings.executivePhoneNumber || null);
      setExoPhoneConfigured(Boolean(settings.exoPhone));
    } catch {
      setExecutivePhone(null);
      setExoPhoneConfigured(false);
    }
  }, []);

  const handleCallBack = async (rowId: string, callerNumber: string | null) => {
    const to = normalizePhoneForCall(callerNumber);
    const from = executivePhone ? normalizePhoneForCall(executivePhone) : null;

    if (!from) {
      showCallAlert({
        tone: 'warning',
        title: 'Setup required',
        message: 'Set Executive phone in Settings (Inbound IVR routing section) before calling back.',
      });
      return;
    }
    if (!exoPhoneConfigured) {
      showCallAlert({
        tone: 'warning',
        title: 'Setup required',
        message: 'Configure ExoPhone (Caller ID) in Settings before calling back.',
      });
      return;
    }
    if (!to) {
      showCallAlert({
        tone: 'error',
        title: 'Invalid number',
        message: 'Caller number is missing or invalid.',
      });
      return;
    }

    try {
      setCallingRowId(rowId);
      const result = await connectCommunicationsCall({ from, to, record: true });
      showCallAlert({
        tone: 'success',
        title: 'Call initiated',
        message: `Calling ${callerNumber}. Your executive phone will ring first.${
          result.callSid ? `\n\nCall SID: ${result.callSid}` : ''
        }`,
      });
    } catch (err: unknown) {
      showCallAlert({
        tone: 'error',
        title: 'Call failed',
        message: err instanceof Error ? err.message : 'Failed to place call. Please try again.',
      });
    } finally {
      setCallingRowId(null);
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCommunicationsActivity(days);
      setCallRows(data.callLogs.rows);
      setCallTotals(data.callLogs.totals);
      setCallSource(data.callLogs.source);
      setCallFetchError(data.callLogs.fetchError);
      setIvrRows(data.ivrEvents.rows);
      setIvrTotals(data.ivrEvents.totals);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load call activity.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void loadSettings();
    void load();
  }, [load, loadSettings]);

  const filteredCallRows = useMemo(() => {
    if (!callSearch.trim()) return callRows;
    const q = callSearch.toLowerCase();
    return callRows.filter(
      (r) =>
        (r.callSid || '').toLowerCase().includes(q) ||
        (r.fromNumber || '').toLowerCase().includes(q) ||
        (r.toNumber || '').toLowerCase().includes(q) ||
        (r.callStatus || '').toLowerCase().includes(q) ||
        (r.direction || '').toLowerCase().includes(q)
    );
  }, [callRows, callSearch]);

  const filteredIvrRows = useMemo(() => {
    if (!ivrSearch.trim()) return ivrRows;
    const q = ivrSearch.toLowerCase();
    return ivrRows.filter(
      (r) =>
        (r.callSid || '').toLowerCase().includes(q) ||
        (r.fromNumber || '').toLowerCase().includes(q) ||
        (r.toNumber || '').toLowerCase().includes(q) ||
        (r.digits || '').toLowerCase().includes(q) ||
        (r.actionTaken || '').toLowerCase().includes(q)
    );
  }, [ivrRows, ivrSearch]);

  return (
    <>
      <CommunicationsAlertDialog
        open={Boolean(callAlert)}
        tone={callAlert?.tone || 'error'}
        title={callAlert?.title || ''}
        message={callAlert?.message || ''}
        onClose={() => setCallAlert(null)}
      />

      <div className="space-y-8">
        <div className={commToolbarClass}>
          <label className="flex w-full items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:w-auto">
            <span className="shrink-0">Range</span>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={commSelectClass}>
              {DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  Last {d} days
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => void load()} disabled={loading} className={commSecondaryBtnClass}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
            {error}
          </div>
        ) : null}

        <section className="space-y-4">
          <div>
            <h2 className={commSectionTitleClass}>Call logs</h2>
            <p className={commSectionDescClass}>
              Actual calls from Exotel ({callSource === 'exotel_api' ? 'live API' : 'local fallback'}).
            </p>
          </div>

          {callFetchError ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              {callFetchError}
            </div>
          ) : null}

          {callTotals && !loading ? (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <StatCard label="Total calls" value={callTotals.total} />
              <StatCard label="Completed" value={callTotals.completed} />
              <StatCard label="Incoming" value={callTotals.inbound} />
              <StatCard label="Outgoing" value={callTotals.outbound} />
              <StatCard label="Avg duration" valueText={formatDuration(callTotals.avgDurationSeconds)} />
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : null}

          <DataTable
            searchPlaceholder="Search call logs by SID, phone, status…"
            searchValue={callSearch}
            onSearchChange={setCallSearch}
            loading={loading}
            emptyMessage="No call logs in this range. Place a call or check Exotel credentials in Settings."
            columns={['From', 'To', 'Direction', 'Duration', 'Status', 'Started', '']}
            rows={filteredCallRows}
            renderMobileCard={(r) => {
              const incoming = isIncomingCall(r.direction);
              const callerNumber = incoming ? r.fromNumber : null;
              const canCall = incoming && Boolean(normalizePhoneForCall(callerNumber));
              const isCalling = callingRowId === r.id;

              return (
                <div key={r.id} className={commMobileCardClass}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase text-gray-500">From</p>
                      <p className="break-all text-sm font-medium text-gray-900 dark:text-white">
                        {r.fromNumber || '—'}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                      {formatDirection(r.direction)}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500">To</p>
                      <p className="break-all text-gray-700 dark:text-gray-300">{r.toNumber || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500">Duration</p>
                      <p className="tabular-nums text-gray-900 dark:text-white">{formatDuration(r.duration)}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {r.callStatus || '—'} ·{' '}
                    {r.startTime ? new Date(r.startTime).toLocaleString() : r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                  </p>
                  {r.recordingUrl ? (
                    <a
                      href={r.recordingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600 hover:underline"
                    >
                      Recording <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}
                  {canCall ? (
                    <button
                      type="button"
                      disabled={isCalling}
                      onClick={() => void handleCallBack(r.id, callerNumber)}
                      className={`${commPrimaryBtnClass} mt-3`}
                    >
                      {isCalling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                      Call back
                    </button>
                  ) : null}
                </div>
              );
            }}
            renderRow={(r) => {
              const incoming = isIncomingCall(r.direction);
              const callerNumber = incoming ? r.fromNumber : null;
              const canCall = incoming && Boolean(normalizePhoneForCall(callerNumber));
              const isCalling = callingRowId === r.id;

              return (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900 dark:text-white">{r.fromNumber || '—'}</div>
                    <div className="mt-0.5 max-w-xs truncate font-mono text-xs text-gray-500">{r.callSid || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.toNumber || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{formatDirection(r.direction)}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white">
                    {formatDuration(r.duration)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{r.callStatus || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                    <div>
                      {r.startTime
                        ? new Date(r.startTime).toLocaleString()
                        : r.createdAt
                          ? new Date(r.createdAt).toLocaleString()
                          : '—'}
                    </div>
                    {r.recordingUrl ? (
                      <a
                        href={r.recordingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-primary-600 hover:underline"
                      >
                        Recording <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canCall ? (
                      <button
                        type="button"
                        disabled={isCalling}
                        onClick={() => void handleCallBack(r.id, callerNumber)}
                        className={`${commPrimaryBtnClass} !w-auto !px-2.5 !py-1.5 text-xs`}
                        title="Call back this incoming caller"
                      >
                        {isCalling ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Phone className="h-3.5 w-3.5" />
                        )}
                        Call
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            }}
          />
        </section>

        <section className={`space-y-4 ${commCardClass}`}>
          <div>
            <h2 className={commSectionTitleClass}>IVR webhook events</h2>
            <p className={commSectionDescClass}>
              Inbound IVR Passthru triggers (digit presses, WhatsApp requests) — not full call records.
            </p>
          </div>

          {ivrTotals && !loading ? (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <StatCard label="IVR triggers" value={ivrTotals.total} />
              <StatCard label="WhatsApp requested" value={ivrTotals.whatsappRequested} />
              <StatCard label="Digit 1" value={ivrTotals.digitOne} />
              <StatCard label="Other digits" value={ivrTotals.otherDigits} />
              <StatCard label="No action" value={ivrTotals.noAction} />
            </div>
          ) : null}

          <DataTable
            searchPlaceholder="Search IVR events by phone, digits, action…"
            searchValue={ivrSearch}
            onSearchChange={setIvrSearch}
            loading={loading}
            emptyMessage="No IVR webhook events in this range."
            columns={['Caller', 'To', 'Digits', 'Action', 'Status', 'Received', '']}
            rows={filteredIvrRows}
            renderMobileCard={(r) => {
              const canCall = Boolean(normalizePhoneForCall(r.fromNumber));
              const isCalling = callingRowId === `ivr-${r.id}`;

              return (
                <div key={r.id} className={commMobileCardClass}>
                  <div>
                    <p className="text-xs font-medium uppercase text-gray-500">Caller</p>
                    <p className="break-all text-sm font-medium text-gray-900 dark:text-white">
                      {r.fromNumber || '—'}
                    </p>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500">Digits</p>
                      <p className="font-semibold tabular-nums text-gray-900 dark:text-white">{r.digits || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium uppercase text-gray-500">Action</p>
                      <p className="text-gray-700 dark:text-gray-300">{formatAction(r.actionTaken)}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                  </p>
                  {canCall ? (
                    <button
                      type="button"
                      disabled={isCalling}
                      onClick={() => void handleCallBack(`ivr-${r.id}`, r.fromNumber)}
                      className={`${commPrimaryBtnClass} mt-3`}
                    >
                      {isCalling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                      Call
                    </button>
                  ) : null}
                </div>
              );
            }}
            renderRow={(r) => {
              const canCall = Boolean(normalizePhoneForCall(r.fromNumber));
              const isCalling = callingRowId === `ivr-${r.id}`;

              return (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900 dark:text-white">{r.fromNumber || '—'}</div>
                    <div className="mt-0.5 max-w-xs truncate font-mono text-xs text-gray-500">{r.callSid || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.toNumber || '—'}</td>
                  <td className="px-4 py-3 text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                    {r.digits || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatAction(r.actionTaken)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{r.callStatus || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                    {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canCall ? (
                      <button
                        type="button"
                        disabled={isCalling}
                        onClick={() => void handleCallBack(`ivr-${r.id}`, r.fromNumber)}
                        className={`${commPrimaryBtnClass} !w-auto !px-2.5 !py-1.5 text-xs`}
                        title="Call this incoming caller"
                      >
                        {isCalling ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Phone className="h-3.5 w-3.5" />
                        )}
                        Call
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            }}
          />
        </section>
      </div>
    </>
  );
};

function DataTable<T extends { id: string }>({
  columns,
  rows,
  renderRow,
  renderMobileCard,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  loading,
  emptyMessage,
}: {
  columns: string[];
  rows: T[];
  renderRow: (row: T) => React.ReactNode;
  renderMobileCard: (row: T) => React.ReactNode;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (v: string) => void;
  loading: boolean;
  emptyMessage: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-200 p-3 dark:border-gray-700 sm:p-4">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-3 text-base dark:border-gray-600 dark:bg-gray-900 dark:text-white sm:text-sm"
          />
        </div>
      </div>

      <div className="space-y-3 p-3 md:hidden">
        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">{loading ? 'Loading…' : emptyMessage}</p>
        ) : (
          rows.map((row) => renderMobileCard(row))
        )}
      </div>

      <div className={commDesktopTableWrapClass}>
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              {columns.map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-gray-500">
                  {loading ? 'Loading…' : emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => renderRow(row))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  valueText,
}: {
  label: string;
  value?: number;
  valueText?: string;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-4">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</span>
      <div className="mt-1 text-xl font-bold tabular-nums text-gray-900 dark:text-white sm:text-2xl">
        {valueText ?? value ?? 0}
      </div>
    </div>
  );
}

export default CommunicationsActivityPanel;
